import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import Seo from "@/components/Seo";
import Eyebrow from "@/components/site/Eyebrow";
import { ACCENT, NAVY, CREAM } from "@/lib/siteTheme";
import heronIcon from "@/assets/design/blue-heron-icon.webp";

const websiteFeatures = [
  "Custom-built website on your domain (you own the domain)",
  "Launch in 4 weeks or less",
  "Fast, secure, always up to date",
  "Managed hosting with 99.9% uptime",
  "Google Analytics and Search Console setup",
  "Basic on-site SEO built in",
  "Lead capture contact form",
  "Conversion-focused layout (click-to-call and quote CTA above the fold)",
  "Unlimited minor edits within reason",
  "Top-notch support (business hours, Central time)",
  "Keep your site if you leave",
];

const seoPlans = [
  {
    key: "starter",
    name: "SEO Starter",
    tagline: "Show up in the map pack and track your local rankings.",
    price: "$999",
    popular: false,
    groups: [
      {
        title: null,
        items: [
          "Free professional website included (your domain, you own it and keep it)",
          "Complete Google Business Profile setup and monthly management",
          "Weekly GBP posts and monthly photo uploads",
          "Review response within 48 hours",
          "Review request automation (target 5+ new reviews per month)",
          "Local rank tracking with monthly geo-grid reports",
          "Citation cleanup and 30 new citations in first 90 days",
          "Monthly performance report",
          "Built on your domain, on infrastructure you own and keep",
        ],
      },
    ],
  },
  {
    key: "growth",
    name: "SEO Growth",
    tagline: "Content, AI-search work, and measurement to prove it's working.",
    price: "$1,499",
    popular: false,
    groups: [
      { title: "Measurement", items: ["Call tracking with recording", "Lead attribution reporting (which channel drove every call and form)", "Weekly ranking snapshots", "Monthly performance report"] },
      { title: "Content & AI Search", items: ["4 long-form posts per month (1,200+ words, buyer-intent, locally focused)", "Content optimized for AI search (ChatGPT, Claude, Perplexity, Google AI Overviews)", "Robust keyword research and topic mapping", "Monthly editorial calendar"] },
      { title: "Rankings & Authority", items: ["2 new service or location pages per month", "FAQ schema on all key pages", "AI search optimization (LLM summary page, structured answer formatting, entity coverage)", "GeoGrid expansion for local map pack rankings", "2 quality local backlinks per month (earned through outreach)"] },
      { title: null, items: ["Built on your domain, on infrastructure you own and keep"] },
    ],
  },
  {
    key: "pro",
    name: "SEO Pro",
    tagline: "Dedicated attention and the volume to move fast in competitive markets.",
    price: "$2,299",
    popular: false,
    groups: [
      {
        title: null,
        items: [
          "Everything in SEO Growth",
          "Named account manager on your account",
          "Monthly 30-minute strategy call",
          "8 blog posts per month",
          "3 new service or location pages per month",
          "Quarterly refresh of top 10 existing pages",
          "Expanded AI search presence through guest posts",
          "1 guest post or digital PR mention per month",
          "Built on your domain, on infrastructure you own and keep",
        ],
      },
    ],
  },
];

const adsPlans = [
  {
    name: "Ads Starter",
    tagline: "Lean budgets. Clean setup. Leads from day one.",
    price: "$999",
    note: "For ad spend budgets up to $2,000/month",
    features: [
      "Free professional website included (your domain, you own it and keep it)",
      "Google Search campaigns (setup and management)",
      "3 ad variations per ad group with monthly testing",
      "Weekly negative keyword review",
      "Call tracking and conversion tracking",
      "Basic landing page optimized for your campaign",
      "Monthly optimization and reporting",
    ],
  },
  {
    name: "Ads Growth",
    tagline: "Scale lead flow with advanced campaign types and weekly optimization.",
    price: "$1,499",
    note: "For ad spend budgets $2,000–$10,000/month",
    features: [
      "Everything in Ads Starter",
      "Performance Max campaign",
      "Remarketing and competitor targeting",
      "2 landing page variants with A/B testing",
      "Weekly optimization",
      "Automated bidding (tCPA/tROAS)",
      "Advanced conversion tracking (calls, forms, offline)",
      "Monthly 30-minute performance call",
    ],
  },
  {
    name: "Ads Pro",
    tagline: "Full campaign suite for businesses running serious ad spend.",
    price: "$2,299",
    note: "For ad spend budgets $10,000+/month",
    features: [
      "Everything in Ads Growth",
      "Multi-campaign management (Search, Performance Max, LSAs, YouTube, Display)",
      "LSA verification support and lead dispute management",
      "Unlimited landing page variants",
      "Monthly CRO recommendations",
      "Conversion value modeling and ROAS targeting",
      "Offline conversion imports",
      "Bi-weekly strategy calls",
      "Weekly performance updates",
    ],
  },
];

const tickerText = "MONTH-TO-MONTH   •   NO LONG-TERM CONTRACTS   •   NO HIDDEN FEES   •   CANCEL ANYTIME   •   ";

const Plans = () => {
  return (
    <Layout active="plans" background={NAVY}>
      <div style={{ fontFamily: "'Archivo', sans-serif", background: NAVY }} className="bh-plans w-full antialiased">
        <Seo
          title="Plans & Pricing | Blue Heron Marketing"
          description="Marketing plans built for home service businesses: websites, local SEO, and Google Ads. Month-to-month, no long-term contracts."
          path="/plans"
        />

        {/* Hero */}
        <section id="top" className="relative overflow-hidden px-6 md:px-14 pt-[120px] pb-16" style={{ background: NAVY }}>
          <div className="absolute -right-[140px] -bottom-10 pointer-events-none" style={{ width: 600, maxWidth: "55vw", opacity: 0.06, transform: "scaleX(-1)" }}>
            <img src={heronIcon} alt="" width={500} height={366} className="w-full h-auto block brightness-0 invert" style={{ animation: "bhFloat 11s ease-in-out infinite" }} />
          </div>
          <div className="max-w-[1280px] mx-auto relative z-10">
            <Eyebrow dark>Plans &amp; Pricing</Eyebrow>
            <h1 className="font-black leading-[0.98] tracking-[-0.02em] mb-7 max-w-[880px]" style={{ color: CREAM, fontSize: "clamp(40px,6vw,80px)" }}>
              Marketing built for home service businesses.
            </h1>
            <p className="max-w-[680px] leading-relaxed" style={{ color: "rgba(247,243,236,0.78)", fontSize: "clamp(17px,1.6vw,20px)" }}>
              Roofers, tree services, and contractors win local work when they show up in Google, Google Maps, and AI search engines like ChatGPT, Claude, and Perplexity. Pick the plan that matches where your business is right now, move up when you're ready.
            </p>
            <div className="flex flex-wrap gap-3.5 mt-10">
              {[
                { href: "#websites", label: "Websites" },
                { href: "#seo", label: "SEO" },
                { href: "#ads", label: "Ads" },
              ].map((a) => (
                <a
                  key={a.href}
                  href={a.href}
                  className="font-bold text-sm px-[22px] py-2.5 rounded-full no-underline transition-colors"
                  style={{ background: "rgba(247,243,236,0.08)", border: "1px solid rgba(247,243,236,.25)", color: CREAM }}
                >
                  {a.label}
                </a>
              ))}
            </div>
          </div>
        </section>
        <div className="relative z-10 w-full overflow-hidden" style={{ background: ACCENT, padding: "14px 0", borderTop: "1px solid rgba(14,40,70,0.15)" }}>
          <div className="flex w-max" style={{ animation: "bhMarquee 22s linear infinite" }}>
            {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
              <span key={i} className="flex items-center whitespace-nowrap font-extrabold text-[15px] pr-8" style={{ color: NAVY, letterSpacing: "0.04em" }}>
                {tickerText}
              </span>
            ))}
          </div>
        </div>

        {/* Websites */}
        <section id="websites" className="px-6 md:px-14 py-[120px] scroll-mt-20" style={{ background: CREAM }}>
          <div className="max-w-[1280px] mx-auto">
            <Eyebrow>Websites</Eyebrow>
            <h2 className="font-black leading-[0.98] tracking-[-0.025em] mb-4 max-w-[640px]" style={{ color: NAVY, fontSize: "clamp(30px,4vw,48px)" }}>
              Every plan starts with a professional website.
            </h2>
            <p className="mb-14 max-w-[560px] text-lg" style={{ color: "rgba(17,33,47,0.65)" }}>Pick this if you just need to look legit online.</p>
            <div className="bg-white rounded-[28px] p-12 grid grid-cols-1 md:grid-cols-[1fr_1.4fr] gap-12 items-start" style={{ border: "1px solid rgba(14,40,70,.08)" }}>
              <div>
                <h3 className="font-extrabold text-[28px] mb-2.5" style={{ color: NAVY }}>Website Only</h3>
                <p className="mb-7 text-base leading-snug" style={{ color: "rgba(17,33,47,0.6)" }}>For the business that needs a professional web presence and nothing more.</p>
                <div className="flex items-baseline gap-1.5 mb-7">
                  <span className="text-[44px] font-black" style={{ color: NAVY }}>$199</span>
                  <span className="font-semibold text-[15px]" style={{ color: "rgba(17,33,47,0.5)" }}>/month</span>
                </div>
                <Link to="/book" className="inline-flex items-center gap-2 font-extrabold text-[15px] px-7 py-[15px] rounded-full no-underline transition-transform hover:-translate-y-0.5" style={{ background: ACCENT, color: NAVY }}>
                  Get Started →
                </Link>
              </div>
              <div className="columns-1 sm:columns-2 gap-8">
                {websiteFeatures.map((f) => (
                  <div key={f} className="flex gap-2.5 items-start break-inside-avoid mb-3.5">
                    <span className="font-extrabold flex-none" style={{ color: ACCENT }}>✓</span>
                    <span className="text-[15px] leading-snug" style={{ color: "rgba(17,33,47,0.75)" }}>{f}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* SEO */}
        <section id="seo" className="px-6 md:px-14 py-[120px] scroll-mt-20" style={{ background: NAVY }}>
          <div className="max-w-[1280px] mx-auto">
            <Eyebrow dark>SEO Plans</Eyebrow>
            <h2 className="font-black leading-[0.98] tracking-[-0.025em] mb-4 max-w-[640px]" style={{ color: CREAM, fontSize: "clamp(30px,4vw,48px)" }}>
              Show up when local customers search.
            </h2>
            <p className="mb-16 max-w-[620px] text-lg" style={{ color: "rgba(247,243,236,0.65)" }}>
              SEO plans include your website plus everything you need to rank. Each plan builds on the one before it.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-7 items-start">
              {seoPlans.map((plan) => (
                <div
                  key={plan.key}
                  className="relative bg-white rounded-[28px] px-9 py-11 flex flex-col h-full"
                  style={{
                    border: plan.popular ? `2px solid ${ACCENT}` : "1px solid rgba(14,40,70,.08)",
                    boxShadow: plan.popular ? "0 28px 56px rgba(14,40,70,.16)" : "none",
                    transform: plan.popular ? "scale(1.03)" : "none",
                  }}
                >
                  {plan.popular && (
                    <span
                      className="absolute -top-3.5 left-9 font-extrabold text-[11px] tracking-[0.07em] uppercase px-4 py-1.5 rounded-full"
                      style={{ background: ACCENT, color: NAVY }}
                    >
                      Most Popular
                    </span>
                  )}
                  <h3 className="font-extrabold text-2xl mb-2" style={{ color: NAVY }}>{plan.name}</h3>
                  <p className="mb-5 text-[15px] leading-snug" style={{ color: "rgba(17,33,47,0.6)", minHeight: 46 }}>{plan.tagline}</p>
                  <div className="flex items-baseline gap-1.5 mb-6">
                    <span className="text-[38px] font-black" style={{ color: NAVY }}>{plan.price}</span>
                    <span className="font-semibold text-sm" style={{ color: "rgba(17,33,47,0.5)" }}>/month</span>
                  </div>
                  <div className="flex-1 flex flex-col gap-[18px] mb-7">
                    {plan.groups.map((grp, gi) => (
                      <div key={gi}>
                        {grp.title && (
                          <div className="font-extrabold text-[11px] tracking-[0.08em] uppercase mb-2.5" style={{ color: "#9c5e15" }}>{grp.title}</div>
                        )}
                        <div className="flex flex-col gap-2.5">
                          {grp.items.map((item) => (
                            <div key={item} className="flex gap-2 items-start">
                              <span className="font-extrabold flex-none text-sm" style={{ color: ACCENT }}>✓</span>
                              <span className="text-[14.5px] leading-snug" style={{ color: "rgba(17,33,47,0.75)" }}>{item}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  <Link
                    to="/book"
                    className="flex items-center justify-center gap-2 font-extrabold text-[15px] px-7 py-[15px] rounded-full no-underline mt-auto transition-transform hover:-translate-y-0.5"
                    style={{ background: plan.popular ? ACCENT : NAVY, color: plan.popular ? NAVY : CREAM }}
                  >
                    Get Started →
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Ads */}
        <section id="ads" className="px-6 md:px-14 py-[120px] scroll-mt-20" style={{ background: CREAM }}>
          <div className="max-w-[1280px] mx-auto">
            <Eyebrow>Paid Ads Plans</Eyebrow>
            <h2 className="font-black leading-[0.98] tracking-[-0.025em] mb-4 max-w-[640px]" style={{ color: NAVY, fontSize: "clamp(30px,4vw,48px)" }}>
              Ads that drive calls, not just clicks.
            </h2>
            <p className="mb-16 max-w-[620px] text-lg" style={{ color: "rgba(17,33,47,0.65)" }}>
              Designed to generate calls and form fills. Ad spend is billed separately by Google.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-7 items-start">
              {adsPlans.map((plan) => (
                <div key={plan.name} className="relative bg-white rounded-[28px] px-9 py-11 flex flex-col h-full" style={{ border: "1px solid rgba(14,40,70,.08)" }}>
                  <h3 className="font-extrabold text-2xl mb-2" style={{ color: NAVY }}>{plan.name}</h3>
                  <p className="mb-5 text-[15px] leading-snug" style={{ color: "rgba(17,33,47,0.6)", minHeight: 46 }}>{plan.tagline}</p>
                  <div className="flex items-baseline gap-1.5 mb-6">
                    <span className="text-[38px] font-black" style={{ color: NAVY }}>{plan.price}</span>
                    <span className="font-semibold text-sm" style={{ color: "rgba(17,33,47,0.5)" }}>/month</span>
                  </div>
                  <div className="flex-1 flex flex-col gap-2.5 mb-5">
                    {plan.features.map((f) => (
                      <div key={f} className="flex gap-2 items-start">
                        <span className="font-extrabold flex-none text-sm" style={{ color: ACCENT }}>✓</span>
                        <span className="text-[14.5px] leading-snug" style={{ color: "rgba(17,33,47,0.75)" }}>{f}</span>
                      </div>
                    ))}
                  </div>
                  <p className="mb-6 text-[13px] italic" style={{ color: "rgba(17,33,47,0.5)" }}>{plan.note}</p>
                  <Link
                    to="/book"
                    className="flex items-center justify-center gap-2 font-extrabold text-[15px] px-7 py-[15px] rounded-full no-underline mt-auto transition-transform hover:-translate-y-0.5"
                    style={{ background: ACCENT, color: NAVY }}
                  >
                    Get Started →
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Bundle banner */}
        <section className="px-6 md:px-14 py-16 text-center" style={{ background: ACCENT }}>
          <div className="max-w-[760px] mx-auto">
            <h2 className="font-black leading-[1.05] tracking-[-0.02em] mb-3.5" style={{ color: NAVY, fontSize: "clamp(28px,3.4vw,42px)" }}>
              Bundle SEO + Ads and save 10%.
            </h2>
            <p className="mb-8 text-[17px] leading-snug" style={{ color: "rgba(14,40,70,0.75)" }}>
              Mix and match any SEO plan with any Ads plan. The 10% discount applies to the lower-priced service.
            </p>
            <Link to="/book" className="inline-flex items-center gap-2 font-extrabold text-[15px] px-[30px] py-4 rounded-full no-underline transition-transform hover:-translate-y-0.5" style={{ background: NAVY, color: ACCENT }}>
              Talk to Luke About Bundles →
            </Link>
          </div>
        </section>

        {/* Referral + final CTA */}
        <section className="px-6 md:px-14 pt-[120px] pb-[150px] text-center" style={{ background: CREAM }}>
          <div className="max-w-[820px] mx-auto">
            <div className="bg-white rounded-3xl px-10 py-9 mb-16 flex flex-wrap gap-5 items-center justify-between text-left" style={{ border: "1px solid rgba(14,40,70,.1)" }}>
              <div>
                <h3 className="font-extrabold text-[22px] mb-1.5" style={{ color: NAVY }}>Refer someone, get $500 off.</h3>
                <p className="max-w-[480px] text-[15px]" style={{ color: "rgba(17,33,47,0.6)" }}>
                  Refer a new client who signs up for any paid SEO or Ads plan and we'll take $500 off your next invoice. No tiers, no cap, no expiration.
                </p>
              </div>
              <Link to="/book" className="flex-none inline-flex items-center gap-2 font-extrabold text-sm px-6 py-3.5 rounded-full no-underline transition-transform hover:-translate-y-0.5" style={{ background: ACCENT, color: NAVY }}>
                Refer a Friend →
              </Link>
            </div>
            <div className="flex flex-col items-center mb-6">
              <div className="font-extrabold text-[13px] tracking-[0.16em] uppercase mb-3" style={{ color: "#9c5e15" }}>Let's Talk</div>
              <div className="w-8 h-[3px] rounded-sm" style={{ background: ACCENT }} />
            </div>
            <h2 className="font-black leading-[0.98] tracking-[-0.025em] mb-6" style={{ color: NAVY, fontSize: "clamp(32px,4.6vw,56px)" }}>
              Not sure which plan is right?
            </h2>
            <p className="mb-4 text-[19px] leading-relaxed" style={{ color: "rgba(17,33,47,0.65)" }}>
              Book a free 30-minute call and I'll help you figure out the best fit for your business and budget.
            </p>
            <p className="mb-10 text-sm leading-relaxed" style={{ color: "rgba(17,33,47,0.45)" }}>
              All plans are month-to-month. 30-day notice to cancel. You own your website and keep it if you leave. Ad spend is billed separately by the ad platform.
            </p>
            <Link
              to="/book"
              className="inline-flex items-center gap-2.5 font-extrabold text-lg px-[38px] py-5 rounded-full no-underline transition-transform hover:-translate-y-1"
              style={{ background: ACCENT, color: NAVY, boxShadow: "0 16px 32px rgba(14,40,70,.16)" }}
            >
              Book A Call →
            </Link>
          </div>
        </section>
      </div>

      <style dangerouslySetInnerHTML={{ __html: `
        .bh-plans h1, .bh-plans h2, .bh-plans h3, .bh-plans h4 {
          font-family: 'Archivo', sans-serif;
        }
        @keyframes bhFloat {
          0% { transform: translateY(0) rotate(-6deg); }
          50% { transform: translateY(-22px) rotate(-4deg); }
          100% { transform: translateY(0) rotate(-6deg); }
        }
        @keyframes bhMarquee {
          from { transform: translateX(0); }
          to { transform: translateX(-12.5%); }
        }
      ` }} />
    </Layout>
  );
};

export default Plans;
